import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { LetDirective } from '@ngrx/component';

import { PrivaFormComponent } from '@priva/components/form';
import { PrivaFormGroupComponent } from '@priva/components/form-group';
import { PrivaInputComponent } from '@priva/components/input';
import { PrivaPopoverComponent } from '@priva/components/popover';
import { PrivaSelectComponent } from '@priva/components/select';
import { PrivaLocalizationModule } from '@priva/localization';

import { Activity } from '../../features/activity-selector/models';
import {
    convertFormValueToNumberOrUndefined,
    isNumberWithPointSeparator,
    validateAmountResultForActivity,
} from '../../utils';
import { ProductBoxRegistration } from '../product-box/models';
import { ProductBoxListFormArrayValue } from './product-box-amount-form-value';
import { ProductBoxAmountStore } from './product-box-amount.store';

function convertFormValueToProductBoxRegistration(
    formValues: Partial<ProductBoxListFormArrayValue>,
): ProductBoxRegistration | undefined {
    const productBoxNumber = convertFormValueToNumberOrUndefined(formValues.productBoxNumber);
    if (productBoxNumber === undefined) return undefined;
    return {
        productBoxNumber,
        amount: convertFormValueToNumberOrUndefined(formValues.amount),
    };
}

@Component({
    selector: 'app-product-box-amount',
    templateUrl: './product-box-amount.component.html',
    styleUrl: './product-box-amount.component.scss',
    imports: [
        AsyncPipe,
        FormsModule,
        LetDirective,
        NgSelectModule,
        ReactiveFormsModule,
        PrivaFormGroupComponent,
        PrivaFormComponent,
        PrivaInputComponent,
        PrivaLocalizationModule,
        PrivaPopoverComponent,
        PrivaSelectComponent,
    ],
    providers: [ProductBoxAmountStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductBoxAmountComponent {
    private destroyRef = inject(DestroyRef);
    readonly activityControl = new FormControl<Activity | undefined>(undefined);
    private componentStore = inject(ProductBoxAmountStore);
    private fb = inject(FormBuilder);

    activityAndProductBoxRegistrations = input(undefined, {
        transform: (
            value: {
                activity: Activity | undefined;
                productBoxRegistrations: ProductBoxRegistration[];
            } | null,
        ) => {
            this.componentStore.updateActivityAndProductBoxRegistrations({
                activity: value?.activity,
                productBoxRegistrations: value?.productBoxRegistrations ?? [],
            });
        },
    });

    productBoxRegistrationsUpdated = output<{
        productBoxRegistrations: ProductBoxRegistration[];
        hasError: boolean;
    }>();

    productBoxes$ = this.componentStore.productBoxes$;
    hasDefaultProductBox$ = this.componentStore.hasDefaultProductBox$;
    hasDefaultProductBoxDefined$ = this.componentStore.hasDefaultProductBoxDefined$;

    productBoxFormGroup = this.fb.group({
        activity: this.activityControl,
        productBoxList: this.fb.array([]),
    });

    get productBoxList(): FormArray {
        return this.productBoxFormGroup.get('productBoxList') as FormArray;
    }

    readonly amountInput = viewChildren('amountInput', { read: ElementRef });

    constructor() {
        this.setFormValuesWithProductBoxes();

        this.productBoxList.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            const productBoxRegistrations: ProductBoxRegistration[] = [];

            this.productBoxList.value.forEach((productBox: ProductBoxListFormArrayValue) => {
                if (isNumberWithPointSeparator(productBox.amount ?? '')) {
                    const p = convertFormValueToProductBoxRegistration(productBox);
                    if (p) productBoxRegistrations.push(p);
                }
            });
            this.productBoxRegistrationsUpdated.emit({
                productBoxRegistrations,
                hasError: !(this.productBoxList.status === 'VALID'),
            });
        });
    }

    // Set and update productBoxFormGroup values there's a change based on new activity or productBoxRegistrations
    // If there's no change in activity selected, update form values (productBox with amount)
    // If there's a new activity selected, clear previous productBoxList form array controls and build a productBoxList
    private setFormValuesWithProductBoxes() {
        this.componentStore.productBoxes$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((productBoxes) => {
                if (!productBoxes.activity?.number) return;
                const activityFormGroup = this.productBoxFormGroup.value.activity as Activity;
                if (
                    activityFormGroup?.number === productBoxes.activity?.number &&
                    this.productBoxList.value.length > 0
                ) {
                    this.productBoxList.setValue(
                        productBoxes.productBoxList ?? [
                            {
                                amount: '',
                                productBoxName: '',
                                productBoxNumber: '',
                            },
                        ],
                    );
                    return;
                }

                this.productBoxFormGroup.patchValue({ activity: productBoxes.activity });
                // Clear is required for every activity selected may have a different set of product boxes
                this.productBoxList.clear();

                if (productBoxes.hasDefaultProductBoxNumber) {
                    productBoxes.productBoxList?.forEach((product) => {
                        this.productBoxList.push(this.buildProductBoxList(product));
                    });
                } else {
                    // Set default value from defaultProductBoxList from activity
                    this.productBoxList.push(
                        this.buildProductBoxList({
                            amount: productBoxes.productBoxList[0].amount ?? '',
                            productBoxName: '',
                            productBoxNumber:
                                productBoxes.recentProductBoxNumber.toString() ??
                                productBoxes.privaSelectOption[0].value,
                        }),
                    );
                }
            });
    }

    private buildProductBoxList(product?: ProductBoxListFormArrayValue) {
        return this.fb.group(
            {
                amount: product?.amount ?? '',
                productBoxName: product?.productBoxName ?? '',
                productBoxNumber: product?.productBoxNumber ?? '',
            },
            {
                validators: [
                    (formGroup: AbstractControl) => {
                        const { activity } = this.productBoxFormGroup.value;
                        const { amount } = formGroup.value;
                        return validateAmountResultForActivity({
                            activity: activity ?? undefined,
                            amountString: amount ?? '',
                        });
                    },
                ],
            },
        );
    }

    blurInput(i: number) {
        return this.amountInput()?.at(i)?.nativeElement?.blur();
    }
}
